import React, { useState, useEffect } from 'react';
import { Input, Button, Card, CardBody, Switch } from '@nextui-org/react';
import { Icon } from '@iconify/react';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { metrics__dayblock_tasks_number } from 'common/src/api/metrics';

interface TaskStat {
  date: string;
  hour: number;
  task_count: number;
  percentage: number;
}

const TaskersStatsTable: React.FC = () => {
  const [taskStats, setTaskStats] = useState<TaskStat[]>([]);
  const [startDate, setStartDate] = useState('2024-08-27');
  const [endDate, setEndDate] = useState('2024-09-07');
  const [tempStartDate, setTempStartDate] = useState(startDate);
  const [tempEndDate, setTempEndDate] = useState(endDate);
  const [loading, setLoading] = useState(false);
  const [showPercentage, setShowPercentage] = useState(false);

  const fetchTaskStats = async (start: string, end: string) => {
    setLoading(true);
    try {
      const { data, error } = await metrics__dayblock_tasks_number(start, end);
      if (error) throw error;
      return data as TaskStat[];
    } catch (error) {
      console.error('Error fetching task stats:', error);
      return [];
    } finally {
      setLoading(false);
    }
  };

  const loadTaskStats = async () => {
    const data = await fetchTaskStats(startDate, endDate);
    setTaskStats(data);
  };

  useEffect(() => {
    loadTaskStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate]);

  const handleDateSubmit = () => {
    setStartDate(tempStartDate);
    setEndDate(tempEndDate);
  };

  const exportToExcel = () => {
    const fileType =
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
    const fileExtension = '.xlsx';

    const ws = XLSX.utils.json_to_sheet(taskStats);
    const wb = { Sheets: { data: ws }, SheetNames: ['data'] };
    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const data = new Blob([excelBuffer], { type: fileType });
    saveAs(data, `task_stats_${startDate}_${endDate}${fileExtension}`);
  };

  const getColorIntensity = (value: number, max: number) => {
    const intensity = Math.min(Math.floor((value / max) * 255), 255);
    return `rgb(255, ${255 - intensity}, ${255 - intensity})`;
  };

  const formatHour = (hour: number) => {
    const ampm = hour < 12 ? 'AM' : 'PM';
    const formattedHour = hour % 12 || 12;
    return `${formattedHour.toString().padStart(2, '0')}:00 ${ampm}`;
  };

  const renderHeatmap = () => {
    if (taskStats.length === 0) return null;

    const dates = [...new Set(taskStats.map((stat) => stat.date))];
    const hours = Array.from({ length: 24 }, (_, i) => i);

    const maxValue = Math.max(
      ...taskStats.map((stat) => (showPercentage ? stat.percentage : stat.task_count))
    );

    return (
      <div className="relative">
        <div className="overflow-x-auto max-w-full">
          <div className="w-[max-content]">
            <table className="border-collapse text-xs">
              <thead>
                <tr>
                  <th className="p-1 border">Hora</th>
                  {dates.map((date) => (
                    <th key={date} className="p-1 border">
                      {new Date(date).toLocaleDateString('es-ES', {
                        weekday: 'short',
                        month: '2-digit',
                        day: '2-digit',
                      })}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {hours.map((hour) => (
                  <tr key={hour}>
                    <td className="p-1 border text-center">{formatHour(hour)}</td>
                    {dates.map((date) => {
                      const stat = taskStats.find((s) => s.date === date && s.hour === hour);
                      const value = stat ? (showPercentage ? stat.percentage : stat.task_count) : 0;
                      const backgroundColor = getColorIntensity(value, maxValue);
                      return (
                        <td
                          key={`${date}-${hour}`}
                          className="p-1 border text-center"
                          style={{ backgroundColor }}
                          title={`${formatHour(hour)}: ${value.toFixed(showPercentage ? 2 : 0)}${
                            showPercentage ? '%' : ''
                          }`}
                        >
                          {value.toFixed(showPercentage ? 1 : 0)}
                          {showPercentage ? '%' : ''}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div className="absolute top-0 right-0 bottom-0 w-8 bg-gradient-to-l from-black/25 to-transparent pointer-events-none"></div>
      </div>
    );
  };

  return (
    <div className="max-w-4xl mx-auto">
      <Card className="mb-6">
        <CardBody>
          <h3 className="text-lg font-semibold mb-3">Seleccionar Rango de Fechas</h3>
          <div className="flex flex-col sm:flex-row gap-4 items-end">
            <div className="w-full sm:w-1/3">
              <Input
                label="Fecha de Inicio"
                type="date"
                value={tempStartDate}
                onChange={(e) => setTempStartDate(e.target.value)}
                startContent={<Icon icon="mdi:calendar" className="text-gray-400" />}
                aria-label="Fecha de Inicio"
              />
            </div>
            <div className="w-full sm:w-1/3">
              <Input
                label="Fecha de Fin"
                type="date"
                value={tempEndDate}
                onChange={(e) => setTempEndDate(e.target.value)}
                startContent={<Icon icon="mdi:calendar" className="text-gray-400" />}
                aria-label="Fecha de Fin"
              />
            </div>
            <Button
              color="primary"
              onClick={handleDateSubmit}
              className="w-full sm:w-auto px-6"
              isLoading={loading}
            >
              Aplicar Rango
            </Button>
          </div>
        </CardBody>
      </Card>

      <Card className="mb-6">
        <CardBody>
          <h3 className="text-lg font-semibold mb-3">Opciones Adicionales</h3>
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <Button
              onClick={exportToExcel}
              className="w-full sm:w-auto px-6 bg-green-600 text-white hover:bg-green-700"
              startContent={<Icon icon="mdi:microsoft-excel" className="text-xl" />}
            >
              Exportar a Excel
            </Button>
            <div className="flex items-center">
              <span className="mr-2">Mostrar:</span>
              <Switch checked={showPercentage} onChange={() => setShowPercentage(!showPercentage)}>
                {showPercentage ? 'Porcentaje' : 'Cantidad'}
              </Switch>
            </div>
          </div>
        </CardBody>
      </Card>

      {renderHeatmap()}
    </div>
  );
};

export default TaskersStatsTable;
